import { Flex } from 'antd';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Switch, Text } from '../Ant';
import { DefaultViewList, FeatureList } from '../../assets/styles/components/featureBox';
import { SettingContent } from '../../assets/styles/components/settingsPopover.styled';
import { Home, LinearBriefcase, LockFilled, ProfileUserOutline } from '../icons';
import { usePage } from '../../context/PageContext';
import { useNavigationStack } from '../../context/NavigationStackContext';
import { useFeatureSelection } from '../../hooks/useFeatureSelection';
import { useListViewPreference } from '../../context/ListViewPreferenceContext';
import AddEditTransactionHeader from '../dashboard/addTransaction/addEditTransactionHeader';
import StartModuleSelect from '../startModuleSelect/startModuleSelect';

const Preferences = () => {
  const { t } = useTranslation();
  const { setTitle } = usePage();
  const { goBack } = useNavigationStack();
  const { preference, updatePreference } = useListViewPreference();
  const {
    features,
    featureStates,
    handleToggle,
    isAllEnabled,
    handleEnableAllToggle,
    activeFeatures,
    startModule,
    handleStartModuleChange,
  } = useFeatureSelection({ autoSave: true });

  const handleBack = () => {
    goBack();
  };

  useEffect(() => {
    setTitle(
      <AddEditTransactionHeader
        title={t('common:profile.preferences')}
        showMenu
        showSettings
        showHomeWithBackButton={false}
        handleBack={handleBack}
      />,
    );
  }, []);

  const defaultViewRows = [
    {
      id: 'matters',
      icon: <LinearBriefcase className="text-clr-general-primary fz-20 flex-shrink-0" />,
      title: t('common:preferences.mattersFavoritesTitle'),
      checked: preference.matters,
      onChange: (checked: boolean) => updatePreference({ matters: checked }),
    },
    {
      id: 'customers',
      icon: <ProfileUserOutline className="text-clr-general-primary fz-20 flex-shrink-0" />,
      title: t('common:preferences.customersFavoritesTitle'),
      checked: preference.customers,
      onChange: (checked: boolean) => updatePreference({ customers: checked }),
    },
  ];

  return (
    <SettingContent
      vertical
      gap={15}
      className="flex-grow overflow-y-auto scroll-page-gutter pb-gutter"
    >
      <Flex vertical>
        <Text
          data-testid="default-view-heading"
          $fontsize="12px"
          $color="subtitle"
          $weight={500}
          $lineheight="16px"
          className="default-view-heading"
        >
          {t('common:preferences.defaultView')}
        </Text>
        <DefaultViewList>
          <div className="default-view-row">
            <Flex align="start" gap={10} className="w-full">
              <Home className="text-clr-general-primary fz-20 flex-shrink-0" />
              <Flex vertical gap={10} className="flex-grow min-w-0">
                <Flex vertical>
                  <Text
                    data-testid="default-view-title-start-module"
                    $fontsize="13px"
                    $color="title"
                    $weight={500}
                    $lineheight="18px"
                  >
                    {t('common:features.startModule')}
                  </Text>
                  <Text
                    data-testid="default-view-description-start-module"
                    $fontsize="12px"
                    $lineheight="18px"
                    $color="subtitle"
                  >
                    {t('common:preferences.startModuleDescription', {
                      platform: t('common:officeAddin'),
                    })}
                  </Text>
                </Flex>
                <StartModuleSelect
                  variant="plain"
                  options={activeFeatures}
                  value={startModule}
                  onChange={handleStartModuleChange}
                />
              </Flex>
            </Flex>
          </div>
          {defaultViewRows.map((row) => (
            <div key={row.id} className="default-view-row">
              <Flex justify="space-between" align="center" gap={10} className="w-full">
                <Flex align="start" gap={10} className="flex-grow">
                  <Flex align="center" className="flex-shrink-0">
                    {row.icon}
                  </Flex>
                  <Flex vertical>
                    <Text
                      data-testid={`default-view-title-${row.id}`}
                      $fontsize="13px"
                      $color="title"
                      $weight={500}
                      $lineheight="18px"
                    >
                      {row.title}
                    </Text>
                    <Text
                      data-testid={`default-view-description-${row.id}`}
                      $fontsize="12px"
                      $lineheight="18px"
                      $color="subtitle"
                    >
                      {row.checked
                        ? t('common:preferences.favoritesOnDescription')
                        : t('common:preferences.favoritesOffDescription')}
                    </Text>
                  </Flex>
                </Flex>
                <Switch
                  data-testid={`default-view-switch-${row.id}`}
                  aria-label={row.title}
                  checked={row.checked}
                  onChange={row.onChange}
                />
              </Flex>
            </div>
          ))}
        </DefaultViewList>
      </Flex>
      <Flex vertical gap={10}>
        <Flex align="center" justify="space-between">
          <Text $fontsize="12px" $color="subtitle" $weight={500} $lineheight="16px">
            {t('common:profile.features')}
          </Text>
          <Flex align="center" gap={8}>
            <Text
              data-testid="feature-selection-enable-all"
              $fontsize="13px"
              $color="primary"
              $weight={500}
            >
              {t('common:features.enableAll')}
            </Text>
            <Switch
              data-testid="enable-all-switch"
              aria-label={t('common:features.enableAll')}
              checked={isAllEnabled}
              onChange={handleEnableAllToggle}
            />
          </Flex>
        </Flex>
        <FeatureList>
          {features.map((feature) => (
            <div key={feature.id} className={`feature-row ${feature.disabled ? 'always-on' : ''}`}>
              <Flex justify="space-between" align="center" className="w-full">
                <Flex gap={10} align="center">
                  {feature.icon}
                  <Text
                    data-testid={`feature-name-${feature?.text}`}
                    $fontsize="13px"
                    $color="title"
                    $weight={500}
                    $lineheight="20px"
                  >
                    {feature.text}
                  </Text>
                  {feature.disabled && (
                    <Flex
                      align="center"
                      gap={4}
                      className="always-on-badge"
                      data-testid={`feature-always-on-${feature?.text}`}
                    >
                      <LockFilled />
                      <Text $fontsize="11px" $weight={500} $color="primary">
                        {t('common:features.alwaysOn')}
                      </Text>
                    </Flex>
                  )}
                </Flex>
                <Switch
                  disabled={feature.disabled}
                  $locked={feature.disabled}
                  data-testid={`feature-switch-${feature?.text}`}
                  aria-label={feature.text}
                  checked={featureStates[feature.id] ?? false}
                  onChange={() => handleToggle(feature.id)}
                />
              </Flex>
            </div>
          ))}
        </FeatureList>
      </Flex>
    </SettingContent>
  );
};

export default Preferences;
